/** @flow */

import { call, put, takeEvery, all, select } from "redux-saga/effects";
import http from "@http";
import config from "@config";
import { addDays } from "date-fns";
import {
  GetAdvisor,
  GetAdvisors,
  SetAdvisorsSort,
  GetAdvisorActivity,
  ExportAdvisorActivity,
} from "@modules/management/constants/actions";
import { transformAdvisorSearch } from "@modules/management/transformers";
import { transformFilters, OPERATORS } from "@transformers/request";
import {
  selectAdvisorsSort,
  selectAdvisorsPage,
  selectAdvisorsFilters,
  selectAdvisorActivityPage,
  selectAdvisorActivityFilters,
} from "@modules/management/selectors/advisor-profile";
import type { Action } from "redux-action";
import actionCreators from "@modules/management/actionCreator";
import { sortToString } from "@transformers/sort";
import { formatDateToInputFormat } from "@transformers/date";
import { downloadCsvFile } from "@sagas/utils";
import { transformAdvisorActivitySearch } from "@modules/management/transformers";

type AdvisorRequest = $Exact<{
  advisorId: string,
}>;

function* onGetAdvisor({ payload }: Action<AdvisorRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.advisorId) {
      throw new Error("Invalid payload provided");
    }

    const response = yield call(http.get, `/api/v1/system-management/user/${payload.advisorId}`);

    yield put(actionCreators.GetAdvisorSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAdvisorError(e.response));
  }
}

type AdvisorsListRequest = $Exact<{
  page: number,
  filters: Object,
}>;

function* onGetAdvisors({ payload }: Action<AdvisorsListRequest>): Generator<*, void, *> {
  const currentPage = yield select(selectAdvisorsPage);
  const filters = yield select(selectAdvisorsFilters);
  const page = (payload && payload.page) || currentPage;
  const sort = yield select(selectAdvisorsSort);
  const transformedFilters = transformAdvisorSearch(filters);

  try {
    const response = yield call(http.get, `/api/v1/system-management/user`, {
      params: {
        page,
        search: transformFilters(transformedFilters, {
          last_name: OPERATORS.STARTS_WITH,
          id: OPERATORS.EQUALS,
        }),
        sort: sortToString(sort),
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.GetAdvisorsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAdvisorsError(e.response));
  }
}

type AdvisorActivityRequest = $Exact<{
  ...AdvisorRequest,
  page?: number,
}>;

function* onGetAdvisorActivity({ payload }: Action<AdvisorActivityRequest>): Generator<*, void, *> {
  if (!payload || !payload.advisorId) {
    throw new Error("Invalid payload provided");
  }

  const currentPage = yield select(selectAdvisorActivityPage);
  const page = (payload && payload.page) || currentPage;
  const filters = yield select(selectAdvisorActivityFilters);

  const transformedFilters = transformAdvisorActivitySearch(filters);

  try {
    const response = yield call(
      http.get,
      `/api/v1/contact-centre/user/${payload.advisorId}/advisor-activity`,
      {
        params: {
          page,
          search: transformedFilters,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetAdvisorActivitySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAdvisorActivityError(e.response));
  }
}

function* onExportAdvisorActivity({ payload }: Action<AdvisorRequest>): Generator<*, void, *> {
  if (!payload || !payload.advisorId) {
    throw new Error("Invalid payload provided");
  }

  const filters = yield select(selectAdvisorActivityFilters);

  const transformedFilters = transformAdvisorActivitySearch(filters);

  try {
    const response = yield call(
      http.get,
      `/api/v1/contact-centre/user/${payload.advisorId}/advisor-activity/export`,
      {
        headers: {
          Accept: "text/csv",
        },
        params: {
          search: transformedFilters,
        },
      },
    );

    downloadCsvFile(response.data, "advisor-activity.csv");

    yield put(actionCreators.ExportAdvisorActivitySuccess());
  } catch (e) {
    yield put(actionCreators.ExportAdvisorActivityError(e.response));
  }
}

export default function* advisorProfileSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAdvisor, onGetAdvisor),
    takeEvery(GetAdvisors, onGetAdvisors),
    takeEvery(SetAdvisorsSort, onGetAdvisors),
    takeEvery(GetAdvisorActivity, onGetAdvisorActivity),
    takeEvery(ExportAdvisorActivity, onExportAdvisorActivity),
  ]);
}
